import Nullable from "@data-force/app/interfaces/nullable";
import {
    CompleteType, Status, Task, TaskEntity,
} from "../../interfaces/store/actions/payload/task";

import {
    ADD_FAILED_FILE_UPLOAD_PATH,
    ADD_UPLOADED_CHUNK,
    CREATE_UPLOADED_CHUNKS_HOLDER,
    INCREMENT_UPLOADED_FILES,
    REMOVE_FAILED_FILE_UPLOAD_PATH,
    SET_ACTIVE_TASK_BY_ID,
    SET_JOB_DETAILS,
    SET_JOB_MASK_DATA,
    SET_JOB_PHASE,
    SET_PHASE_DETAILS,
    SET_TASK_LIST,
    SET_TASK_MASK_DATA,
    UPDATE_ACTIVE_TASK,
    UPDATE_ACTIVE_TASK_PRIVATES,
    UPDATE_FAILED_UPLOAD_DIALOG,
    UPDATE_TASK_ENTITY_BY_ID,
} from "../actions/availableActions";
import {
    AddFailedFileUploadPathAction,
    AddUploadedChunkAction,
    CreateUploadedChunksHolderAction,
    IncrementUploadedFilesAction,
    RemoveFailedFileUploadPathAction,
    SetJobDetailsAction,
    SetJobMaskDataAction,
    SetJobPhaseAction,
    SetPhaseDetailsAction,
    UpdateFailedUploadDialogAction,
} from "../../interfaces/store/actions/job";
import {JobState} from "../../interfaces/store/reducers/jobState";
import {
    SetActiveTaskByIdAction,
    SetTaskListAction,
    SetTaskMaskDataAction,
    UpdateActiveTaskAction,
    UpdateActiveTaskPrivatesAction,
    UpdateTaskEntityByIdAction,
} from "../../interfaces/store/actions/task";

import {
    AddUploadedChunkPayload,
    CreateUploadedChunksHolderPayload,
    MultipartUploads,
    UploadedChunksHolder,
} from "../../interfaces/store/actions/payload/job";
import { PhaseAccessStatus } from "../../enums/enums";

const initialState: JobState = {
    jobDetails: null,
    jobPhase: {
        id: "",
        projectId: "",
        jobId: "",
        name: "",
        taskCount: 0,
        title: "",
        workflowDefinitionId: "",
        phaseStats: {},
        status: Status.READY,
        taskStats: {},
        accessStatus: PhaseAccessStatus.ALLOWED,
    },
    jobMaskData: {
        visible: true,
        messageId: "",
        displaySpinner: false,
        completeType: null,
    },
    uploadedFiles: 0,
    failedFileUploadPaths: [],
    failedUploadDialog: {
        visible: false,
        event: "",
        completeType: CompleteType.BULK,
    },
    multipartUploads: {},
    tasks: [],
    activeTask: null,
    activeTaskPrivates: {
        signedMediaUrl: undefined,
    },
    taskMaskData: {
        visible: true,
    },
    scrollTopFocus: -1,
};

type AvailableActions = SetJobDetailsAction
| SetJobPhaseAction
| SetJobMaskDataAction
| IncrementUploadedFilesAction
| AddFailedFileUploadPathAction
| RemoveFailedFileUploadPathAction
| UpdateFailedUploadDialogAction
| AddUploadedChunkAction
| CreateUploadedChunksHolderAction
| SetTaskListAction
| SetActiveTaskByIdAction
| UpdateActiveTaskPrivatesAction
| UpdateActiveTaskAction
| UpdateTaskEntityByIdAction
| SetTaskMaskDataAction
| SetPhaseDetailsAction;

const jobReducer = (state: JobState = initialState, action: AvailableActions): JobState => {
    switch (action.type) {
        case SET_JOB_DETAILS:
            return {...state, jobDetails: action.payload};
        case SET_JOB_PHASE:
            return {...state, jobPhase: action.payload};
        case SET_PHASE_DETAILS:
            return {
                ...state,
                jobPhase: {
                    ...state.jobPhase,
                    ...action.payload,
                },
            };
        case SET_JOB_MASK_DATA:
            return {...state, jobMaskData: {...state.jobMaskData, ...action.payload}};
        case INCREMENT_UPLOADED_FILES:
            return {...state, uploadedFiles: state.uploadedFiles + 1};
        case ADD_FAILED_FILE_UPLOAD_PATH: {
            const failedUploadPaths: string[] = state.failedFileUploadPaths.slice();
            if (!failedUploadPaths.includes(action.payload)) {
                failedUploadPaths.push(action.payload);
            }
            return {...state, failedFileUploadPaths: failedUploadPaths};
        }
        case REMOVE_FAILED_FILE_UPLOAD_PATH: {
            const failedUploadPaths: string[] = state.failedFileUploadPaths.slice();
            const uploadPathIndex: number = state.failedFileUploadPaths.findIndex(
                (uploadPath: string) => uploadPath === action.payload,
            );
            if (uploadPathIndex >= 0) {
                failedUploadPaths.splice(uploadPathIndex, 1);
            }

            return {...state, failedFileUploadPaths: failedUploadPaths};
        }
        case UPDATE_FAILED_UPLOAD_DIALOG:
            return {...state, failedUploadDialog: {...state.failedUploadDialog, ...action.payload}};
        case CREATE_UPLOADED_CHUNKS_HOLDER: {
            const {multipartId, fileValue}: CreateUploadedChunksHolderPayload = action.payload;
            const chunksHolder: UploadedChunksHolder = {
                multipartId,
                uploadedChunks: [],
            };
            const multipartUploads: MultipartUploads = {...state.multipartUploads};
            multipartUploads[fileValue] = chunksHolder;
            return {...state, multipartUploads: {...state.multipartUploads, ...multipartUploads}};
        }
        case ADD_UPLOADED_CHUNK: {
            const {fileValue, chunk}: AddUploadedChunkPayload = action.payload;
            const multipartUploads: MultipartUploads = {...state.multipartUploads};
            multipartUploads[fileValue].uploadedChunks.push(chunk);

            return {...state, multipartUploads: {...state.multipartUploads, ...multipartUploads}};
        }
        case SET_TASK_LIST:
            return {...state, tasks: action.payload};
        case SET_ACTIVE_TASK_BY_ID: {
            let activeTask: Nullable<Task> = null;
            const taskEntity: Nullable<TaskEntity> = state.tasks.find(
                (item: TaskEntity): boolean => item.payload === action.payload,
            );
            if (taskEntity) {
                const {payload, name, version}: TaskEntity = taskEntity as TaskEntity;
                activeTask = {
                    id: payload,
                    name,
                    version,
                };
            }
            return {
                ...state,
                activeTask,
            };
        }
        case UPDATE_ACTIVE_TASK_PRIVATES:
            return {
                ...state,
                activeTaskPrivates: {
                    ...state.activeTaskPrivates,
                    ...action.payload,
                },
            };
        case UPDATE_ACTIVE_TASK:
            return {
                ...state,
                activeTask: {
                    ...state.activeTask!,
                    ...action.payload.activeTaskUpdate,
                },
            };
        case UPDATE_TASK_ENTITY_BY_ID: {
            const tasks: TaskEntity[] = [...state.tasks];
            const taskToUpdate: Nullable<TaskEntity> = tasks.find(
                (task: TaskEntity): boolean => action.payload.id === task.id,
            );
            if (taskToUpdate) {
                taskToUpdate.status = action.payload.status;
            }
            return {
                ...state,
                tasks,
            };
        }
        case SET_TASK_MASK_DATA:
            return {...state, taskMaskData: {...state.taskMaskData, ...action.payload}};

        default:
            return state;
    }
};

export default jobReducer;
